import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ChangeEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { inSet } from '../../../lib/typeGuards';
import { stateColor } from '../../../theme';
import { offerRequestContactOptionsFor } from '../labels';
import { OfferRequestContactOptions, type OfferRequestContactOption } from '../types';

const CONTACT_OPTION_VALUES = new Set<OfferRequestContactOption>(OfferRequestContactOptions);

export type ContactOptionsFormProps = {
    value?: OfferRequestContactOption;
    isOnOldContract?: boolean;
    onSelect: (option: OfferRequestContactOption) => void;
};

// Picking an option immediately advances the panel (there is no separate submit).
export function ContactOptionsForm({
    value,
    isOnOldContract = false,
    onSelect,
}: ContactOptionsFormProps): ReactElement {
    const { t } = useTranslation();
    const options = offerRequestContactOptionsFor({ isOnOldContract });

    function handleChange(event: ChangeEvent<HTMLInputElement>): void {
        if (inSet(CONTACT_OPTION_VALUES)(event.target.value)) {
            onSelect(event.target.value);
        }
    }

    return (
        <CardContent>
            <Stack spacing={2} alignItems="center">
                <Box
                    aria-hidden
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: (theme) => stateColor(theme, 'focus'),
                        color: 'primary.main',
                    }}>
                    <ChatBubbleOutlineOutlinedIcon />
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle1" component="h3">
                        {t('OFFER_REQUESTS.CONTACT.QUESTION')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t(
                            isOnOldContract
                                ? 'OFFER_REQUESTS.CONTACT.HINT_OLD_CONTRACT'
                                : 'OFFER_REQUESTS.CONTACT.HINT_SUBSCRIPTION',
                        )}
                    </Typography>
                </Box>

                <RadioGroup
                    aria-label={t('OFFER_REQUESTS.CONTACT.OPTIONS_LABEL')}
                    value={value ?? ''}
                    onChange={handleChange}
                    sx={{ gap: 1, width: '100%' }}>
                    {options.map((option) => (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio />}
                            label={t(option.labelKey)}
                            sx={{
                                m: 0,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                pr: 1,
                            }}
                        />
                    ))}
                </RadioGroup>
            </Stack>
        </CardContent>
    );
}
